/** Formatting helpers shared by every screen. */

/** Every time on the platform is shown in the event's zone (India by default),
 * whichever device or server it came from. */
export const EVENT_TZ = 'Asia/Kolkata';

/** The wall-clock parts of an instant, in EVENT_TZ. */
export function zoned(value) {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const p = Object.fromEntries(
    new Intl.DateTimeFormat('en-GB', {
      timeZone: EVENT_TZ, year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, weekday: 'short',
    }).formatToParts(d).map((x) => [x.type, x.value]),
  );
  return { y: +p.year, m: +p.month, d: +p.day, hh: +(p.hour === '24' ? '00' : p.hour), mm: +p.minute, ss: +p.second, weekday: p.weekday };
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function fmtDate(value) {
  const z = value ? zoned(value) : null;
  return z ? `${z.d} ${MONTHS[z.m - 1]} ${z.y}` : '—';
}

export function fmtTime(value) {
  const z = value ? zoned(value) : null;
  if (!z) return '—';
  const ampm = z.hh >= 12 ? 'PM' : 'AM';
  return `${z.hh % 12 || 12}:${String(z.mm).padStart(2, '0')} ${ampm}`;
}

/** An instant as the value a <input type="datetime-local"> expects, in EVENT_TZ. */
export function toInputValue(value) {
  const z = value ? zoned(value) : null;
  if (!z) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${z.y}-${p(z.m)}-${p(z.d)}T${p(z.hh)}:${p(z.mm)}`;
}

export function fmtDateTime(value) {
  if (!value) return '—';
  return `${fmtDate(value)}, ${fmtTime(value)}`;
}

/** "3 hours ago" / "in 2 days" — used by feeds and countdowns. */
export function fmtRelative(value) {
  if (!value) return '—';
  const diff = new Date(value).getTime() - Date.now();
  const abs = Math.abs(diff);
  const mins = Math.round(abs / 60000);
  const past = diff < 0;

  const say = (n, unit) => (past ? `${n} ${unit}${n === 1 ? '' : 's'} ago` : `in ${n} ${unit}${n === 1 ? '' : 's'}`);

  if (mins < 1) return 'just now';
  if (mins < 60) return say(mins, 'min');
  const hours = Math.round(mins / 60);
  if (hours < 24) return say(hours, 'hour');
  const days = Math.round(hours / 24);
  if (days < 30) return say(days, 'day');
  return fmtDate(value);
}

/** Indian-format numbers (1,20,000). */
export function fmtNumber(n) {
  if (n === null || n === undefined) return '—';
  return Number(n).toLocaleString('en-IN');
}

export function initials(name = '') {
  return name.split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();
}

/** Percentage guarded against divide-by-zero. */
export function pct(part, whole) {
  if (!whole) return 0;
  return Math.round((part / whole) * 100);
}

export const EVENT_TYPE_LABEL = {
  conference: 'Conference',
  corporate_event: 'Corporate Event',
  incentive_tour: 'Incentive Tour',
  training: 'Training Program',
  exhibition: 'Exhibition',
  meeting: 'Meeting',
  outdoor: 'Outdoor Event',
};
